import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "primereact/card";
import { BreadCrumb } from 'primereact/breadcrumb';
import type { MenuItem } from "primereact/menuitem";
import { Calendar } from 'primereact/calendar';
import Table from "../components/table";
import { IconCalendar, IconDownload, IconEye, IconFileTypeCsv, IconFileTypeXls } from '@tabler/icons-react';
import { TABLE_COLUMNS_CONFIG, type ColumnMeta } from "../constants/table.cnst";
import type { EVENT_TABLE, EventsApiResponse } from "../interfaces/dashboard";
import { Tag } from "primereact/tag";
import { getSeverity, formatDateInDDMMYYYY, ddmmyyyyToMmddyyyy } from "../services/util.service";
import { Button } from "primereact/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import BarChart from "../components/bar-charts";
import DonutChart from "../components/donut-chart";
import LineChart from "../components/line-chart";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchApi } from "../services/http.service";
import { Dropdown, type DropdownChangeEvent } from "primereact/dropdown";
import { APIMapping } from "../constants/api-mapping.cnst";
import { Skeleton } from "primereact/skeleton";
import { EVENT_CATEGORIES, FILTERS_STORAGE_KEY, HBL_PA_RESOURCES, RCA_BACK_BUTTON } from "../constants/data.cnst";
import { Tooltip } from "primereact/tooltip";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { Menu } from "primereact/menu";
import { TreeSelect } from "primereact/treeselect";


function Dashboard() {
  const eventTableColumns: ColumnMeta[] = TABLE_COLUMNS_CONFIG.EVENTS;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const fromDateParam = searchParams.get("fromDate");
  const toDateParam = searchParams.get("toDate");

  useEffect(() => {
    if (searchParams.get("callApi")) {
      searchParams.delete('callApi');
      setSearchParams(searchParams, { replace: true });
      handleDateChange([pastMonth, today], 'fromNavigation');
    }
  }, [fromDateParam, toDateParam]);

  const fromDate = searchParams.get("fromDate");
  const toDate = searchParams.get("toDate");

  const { today, pastMonth } = useMemo(() => {
    const t = new Date();
    const p = new Date();
    p.setMonth(t.getMonth() - 1);
    return { today: t, pastMonth: p };
  }, []);


  const [dates, setDates] = useState<(Date | null)[]>([
    fromDate ? new Date(ddmmyyyyToMmddyyyy(fromDate)) : pastMonth,
    toDate ? new Date(ddmmyyyyToMmddyyyy(toDate)) : today,
  ]);


  const saveFiltersToStorage = (filters: any) => {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  };

  const getFiltersFromStorage = () => {
    try {
      return JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY) || "{}");
    } catch {
      return {};
    }
  };

  const clearFiltersFromStorage = () => {
    localStorage.removeItem(FILTERS_STORAGE_KEY);
  };

  const saved = getFiltersFromStorage();

  const pageFromUrl = Number(searchParams.get("page") || 1);
  const rowsFromUrl = Number(searchParams.get("rows") || 10);

  const [rows, setRows] = useState(rowsFromUrl);
  const [first, setFirst] = useState((pageFromUrl - 1) * rowsFromUrl);
  const [selectedLocos, setSelectedLocos] = useState(saved?.locos ?? []);
  const [selectedEventInEventsDistributionChart, setSelectedEventInEventsDistributionChart] = useState<string[]>(saved?.eventCategories ?? []);
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>(saved?.subCategories ?? []);
  const [selectedTree, setSelectedTree] = useState<any>(saved?.treeSelection ?? {});

  const eventsStats = [
    { label: "No. of Trips", value: '-', color: "#53D28C", key: "totalTrips", abortedTrips: 0, completedTrips: 0, openTrips: 0, },
    { label: "Events Generated", value: '-', color: "#FFB269", key: "totalEvents" },
    { label: "Critical Events", value: '-', color: "#EA5455", key: "totalCriticalEvents" },
    { label: "Kavach (km)", value: '-', color: "#9B59B6", key: "kavachKmCovered" },
    { label: "Operating Hours", value: '-', color: "#FF69B4", key: "operatingHours" },
    { label: "Availability", value: '-', color: "#5954ea", key: "availability" },
    // { label: "Unified", value: '-', color: "#54eaea", key: "unified" }
  ]


  //trips count api call
  const [tripsSummaryQueryParams, setTripsSummaryQueryParams] = useState({
    fromDate: formatDateInDDMMYYYY(dates[0]),
    toDate: formatDateInDDMMYYYY(dates[1])
  });

  const { data: tripCountsData, isFetching: isTripDataLoading } = useQuery({
    queryKey: ["trips-count", tripsSummaryQueryParams],
    queryFn: () =>
      fetchApi({
        endpoint: APIMapping.TRIPS_COUNTS,
        queryParams: tripsSummaryQueryParams,
        resourceName: HBL_PA_RESOURCES.EVENTS
      }),
    enabled: !!tripsSummaryQueryParams
  });

  const eventsStatistics = useMemo(() => {
    if (!tripCountsData?.result) return eventsStats;

    return eventsStats.map(stat => ({
      ...stat,
      value: tripCountsData?.response?.[stat.key] ?? '-',
      abortedTrips: tripCountsData?.response?.abortedTrips ?? '-',
      completedTrips: tripCountsData?.response?.completedTrips ?? '-',
      openTrips: tripCountsData?.response?.openTrips ?? '-',
    }));
  }, [tripCountsData]);

  // events api call
  const [eventsQueryParams, setEventsQueryParams] = useState({
    fromDate: formatDateInDDMMYYYY(dates[0]),
    toDate: formatDateInDDMMYYYY(dates[1]),
    total_entry_per_page: rowsFromUrl,
    page_num: pageFromUrl,
    locoIds: selectedLocos ?? null,
    eventCategories: selectedEventInEventsDistributionChart?.length
      ? [...new Set(selectedEventInEventsDistributionChart)]?.map((t: string) =>
        t.replaceAll(" ", "_").replaceAll("/", "_").toUpperCase()
      )
      : null,
    subCategories: selectedSubCategories?.length
      ? selectedSubCategories?.map((t: string) =>
        t.toUpperCase()
      )
      : null,
  });


  const { data: eventsApiResponse, isFetching } = useQuery<EventsApiResponse>({
    queryKey: ["events", eventsQueryParams],
    queryFn: () =>
      fetchApi({
        endpoint: APIMapping.EVENTS,
        queryParams: eventsQueryParams,
        resourceName: HBL_PA_RESOURCES.EVENTS
      }),
    enabled: Boolean(eventsQueryParams.fromDate && eventsQueryParams.toDate)
  });

  //sub categories api call
  const { data: eventCategoriesResponse, isFetching: isEventCategoriesLoading } = useQuery({
    queryKey: [
      "event-sub-categories",
      eventsQueryParams.fromDate,
      eventsQueryParams.toDate,
      eventsQueryParams.locoIds,
    ],
    queryFn: () =>
      fetchApi({
        endpoint: APIMapping.GET_EVENT_SUB_CATEGORIES,
        queryParams: { toDate: eventsQueryParams.toDate, fromDate: eventsQueryParams.fromDate, locoIds: eventsQueryParams.locoIds },
        resourceName: HBL_PA_RESOURCES.EVENTS,
      }),
    enabled: Boolean(eventsApiResponse && eventsQueryParams.fromDate && eventsQueryParams.toDate)
  });

  const eventTreeOptions = useMemo(() => {
    return (
      eventCategoriesResponse?.response?.map((item: any) => ({
        key: item.eventCategory,
        label: item.eventCategory
          .replaceAll("_", " ")
          .toLowerCase()
          .replace(/\b\w/g, (c: string) => c.toUpperCase()),
        data: item.eventCategory,
        children: item.subCategories.map((sub: string) => ({
          key: `${item.eventCategory}||${sub}`,
          label: sub,
          data: {
            category: item.eventCategory,
            subCategory: sub,
          },
        })),
      })) ?? []
    );
  }, [eventCategoriesResponse]);

  const eventTableData: EVENT_TABLE[] =
    eventsApiResponse?.response?.data?.map((rowData: EVENT_TABLE) => {
      return {
        ...rowData,
        eventCategory: rowData.eventCategory.replace('_', ' '),
        eventId: rowData.eventId,
        absoluteLocation: rowData.absoluteLocation || 'NA',
        displayEventId: rowData.eventId.substring(0, 5) + '...'
      };
    }) ?? [];

  const totalEvents = eventsApiResponse?.response?.total_entries ?? 0;

  // handle data change and set query params
  const handleDateChange = (value: (Date | null)[], type: string) => {
    setDates(value);
    clearFiltersFromStorage();
    setSelectedEventInEventsDistributionChart([]);
    setSelectedLocos([])
    setSelectedSubCategories([])
    setSelectedTree([])

    if (!value?.[0]) return;

    const payload = {
      fromDate: formatDateInDDMMYYYY(value[0]),
      toDate: formatDateInDDMMYYYY(value[1]) || formatDateInDDMMYYYY(value[0]),
    };
    setFirst(0);
    setRows(10);
    setEventsQueryParams((prev: any) => ({ ...prev, ...payload, page_num: 1, total_entry_per_page: 10, eventCategories: null, locoIds: null, subCategories: null }));
    setTripsSummaryQueryParams((prev: any) => ({ ...prev, ...payload }));
    // setSelectedZone("All");
    if (type === 'fromCalendar') {
      setSearchParams({ fromDate: payload.fromDate, toDate: payload.toDate }, { replace: true });
    }
  };

  const breadcrumb: MenuItem[] = [
    { label: 'Loco Events', style: { pointerEvents: 'none' }, className: 'font-semibold text-gray-900' }
  ];

  const { data: eventsCategoryResponseData } = useQuery({
    queryKey: ["events-category", tripsSummaryQueryParams],
    queryFn: () =>
      fetchApi({
        endpoint: APIMapping.EVENT_SUMMARY,
        queryParams: tripsSummaryQueryParams,
        resourceName: HBL_PA_RESOURCES.EVENTS
      }),
    enabled: !!tripsSummaryQueryParams
  });

  const { data: filtersResponseData } = useQuery({
    queryKey: ["events--filters", tripsSummaryQueryParams],
    queryFn: () =>
      fetchApi({
        endpoint: APIMapping.GET_FILTERS_FOR_EVENTS,
        queryParams: { ...tripsSummaryQueryParams, filterTypes: 'LOCO' },
        resourceName: HBL_PA_RESOURCES.EVENTS
      }),
    enabled: !!tripsSummaryQueryParams
  });

  const locoOptions =
    (filtersResponseData?.response?.locoIds ?? []).map((id: any) => ({
      label: id.toString(),
      value: id.toString(),
    }));

  const { data: eventOverTimeResponseData } = useQuery({
    queryKey: ["events-over-time", tripsSummaryQueryParams],
    queryFn: () =>
      fetchApi({
        endpoint: APIMapping.EVENTS_OVER_TIME,
        queryParams: tripsSummaryQueryParams,
        resourceName: HBL_PA_RESOURCES.EVENTS
      }),
    enabled: !!tripsSummaryQueryParams
  });

  // const { data: eventDistributionResponseData } = useQuery({
  //   queryKey: ["events-distribution-by-zone", tripsSummaryQueryParams],
  //   queryFn: () =>
  //     fetchApi({
  //       endpoint: APIMapping.DISTRIBUTION_BY_ZONE,
  //       queryParams: tripsSummaryQueryParams,
  //       resourceName: HBL_PA_RESOURCES.EVENTS
  //     }),
  //   enabled: !!tripsSummaryQueryParams
  // });

  const getLocoBarChartData = (data: any, key: string) => {
    return {
      labels: data.map((item: any) => String(item[key])),

      datasets: EVENT_CATEGORIES.map((event) => ({
        label: event.label,
        backgroundColor: event.backgroundColor,
        data: data.map(
          (item: any) =>
            item?.breakdownByCategory?.[event.key] ?? 0
        )
      }))
    };
  };

  const periodOptions = useMemo(() => [
    {
      label: "Day",
      value: "Day",
      disabled: eventOverTimeResponseData?.response?.day?.length === 0
    },
    {
      label: "Week",
      value: "Week",
      disabled: eventOverTimeResponseData?.response?.week?.length === 0
    },
    {
      label: "Month",
      value: "Month",
      disabled: eventOverTimeResponseData?.response?.month?.length === 0
    }
  ], [eventOverTimeResponseData]);

  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  useEffect(() => {
    const validOption = periodOptions.find(opt => !opt.disabled);
    setSelectedPeriod(validOption?.value ?? null);
  }, [eventOverTimeResponseData]);


  const getEventOverTimeChartData = (data: any) => {
    const period = selectedPeriod?.toLowerCase() || 'day';
    const periodData = data?.[period] ?? [];
    return {
      labels: periodData.map((item: any) => item.timeBucket),
      dateRange: period === 'day' ? [] : periodData.map((item: any) => item?.dateRange),

      datasets: EVENT_CATEGORIES.filter(event => event.label !== 'Slip/Slide Detected').map((event) => ({
        label: event.label,
        data: periodData.map(
          (item: any) =>
            item?.categoryBreakdown?.[event.key] ?? 0
        ),


        fill: false,
        borderColor: event.backgroundColor,
        tension: 0.4,

        // Point styles
        pointStyle: "circle",
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: event.backgroundColor,
        pointBorderColor: "#fff",
        pointBorderWidth: 2
      }))
    };
  }

  const locoBarChartData = getLocoBarChartData(eventsCategoryResponseData?.response?.topLocosByEvents ?? [], 'locoId');
  // const stationBarChartData = getLocoBarChartData(eventsCategoryResponseData?.response?.topStationsByEvents ?? [], 'stationId');
  const eventsOverTimeData = getEventOverTimeChartData(eventOverTimeResponseData?.response ?? {});

  const eventsDistributionData = {
    labels: EVENT_CATEGORIES.map((e) => e.label),

    datasets: [
      {
        data: EVENT_CATEGORIES.map(
          (e) =>
            eventsCategoryResponseData?.response
              ?.eventDistributionByCategory?.[e.key] ?? 0
        ),

        backgroundColor: EVENT_CATEGORIES.map(
          (e) => e.backgroundColor
        ),

        hoverBackgroundColor: EVENT_CATEGORIES.map(
          (e) => e.backgroundColor
        ),

        borderWidth: 0
      }
    ]
  };

  const getEventColor = (value: string) => {
    return EVENT_CATEGORIES.find(
      (event) => event.label.toUpperCase() === value.replaceAll('_', ' ')
    )?.backgroundColor ?? '#6B7280';
  };



  // const eventPoints: EventPoint[] = RAILWAY_ZONES.map(zone => {
  //   const distribution = eventDistributionResponseData?.response.find((dist: any) => dist.zoneCode === zone.zoneCode);
  //   return {
  //     ...zone,
  //     eventCount: distribution ? distribution.eventCount : 0,
  //     zoneId: distribution ? distribution.zoneId : '-',
  //     disabled: distribution ? distribution.eventCount ? false : true : true
  //   };
  // });

  const handleEventView = (rowData: any) => {
    const params = new URLSearchParams(location.search);
    params.set("eventId", rowData.eventId);
    localStorage.setItem(RCA_BACK_BUTTON, location.pathname + location.search);

    navigate(`/dashboard/event-rca?${params.toString()}`);
  };

  const eventsOverTimeHeader = (
    <div className="flex justify-between items-center">
      <h3 className="m-0 font-semibold">Events Over Time</h3>

      <Dropdown
        value={selectedPeriod}
        onChange={(e: DropdownChangeEvent) => setSelectedPeriod(e.value)}
        options={periodOptions}
        placeholder="Select Period"
        className="w-14rem"
        disabled={eventOverTimeResponseData?.response?.day?.length === 0 && eventOverTimeResponseData?.response?.week?.length === 0 && eventOverTimeResponseData?.response?.month?.length === 0}
      />

    </div>
  );

  // const [selectedZone, setSelectedZone] = useState<string>("All");
  const toast = useRef<Toast>(null);

  const { mutateAsync: downloadEvents, isPending: isDownloading } = useMutation({
    mutationFn: (format: "CSV" | "EXCEL") =>
      fetchApi({
        endpoint: APIMapping.REPORTS_EXPORT,
        responseType: "blob",
        queryParams: { ...eventsQueryParams, format, reportType: "REPORT_TYPE_EVENTS", locoId: eventsQueryParams.locoIds, locoIds: undefined },
        resourceName: HBL_PA_RESOURCES.EVENTS,
      }),
  });

  const handleDownload = async (format: "CSV" | "EXCEL") => {
    try {
      const result = await downloadEvents(format);
      const blob = result.data as Blob;

      const contentDisposition = result.headers.get("content-disposition");
      let fileName = 'REPORT_TYPE_EVENTS'; // fallback

      if (contentDisposition) {
        console.log(contentDisposition)
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          fileName = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: `Loco Events downloaded successfully`,
        life: 3000
      });
    } catch (err) {
      console.error("Download failed", err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Unable to download loco events. Please try again.",
        life: 4000
      });
    }
  };

  const eventItems = [
    {
      label: "CSV",
      icon: <IconFileTypeCsv />,
      command: () => { handleDownload("CSV"); }
    },
    {
      label: "Excel",
      icon: <IconFileTypeXls />,
      command: () => { handleDownload("EXCEL"); }
    }
  ];
  const eventsMenu = useRef<Menu>(null);

  const eventsTableHeader = (

    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <h3 className="m-0 font-semibold">Loco Events</h3>

        {/* {selectedEventInEventsDistributionChart && (
        <Button
          className="bg-gray-200 border-gray-200 text-gray-700 dark:bg-gray-700 dark:border-gray-700 dark:text-gray-200"
          onClick={() => {
            setSelectedEventInEventsDistributionChart('');
            setFirst(0);
            setRows(10);

            setEventsQueryParams((prev: any) => {
              const { eventCategories, ...rest } = prev;
              return { ...rest, page_num: 1, total_entry_per_page: 10 };
            });
          }}
        >
          <span>{selectedEventInEventsDistributionChart}</span>
          <IconX size={20} />
        </Button>
      )} */}

        <Button
          label="Download"
          icon={<IconDownload size={20} />}
          loading={isDownloading}
          disabled={isDownloading || eventTableData.length === 0}
          className="bg-brand-600 border-brand-600 text-white dark:bg-brand-400 dark:border-brand-500"
          onClick={(event) => eventsMenu.current?.toggle(event)}
        />
        <Menu
          popup
          ref={eventsMenu}
          model={eventItems}
          id="popup_menu"
          popupAlignment="right"
        />
      </div>
      <div className="flex justify-end gap-2">

        {/* MultiSelect 1 */}
        <MultiSelect
          value={selectedLocos}
          options={locoOptions ?? []} // [{ label, value }]
          filter
          resetFilterOnHide
          onChange={(e: any) => {
            const selected = e.value;
            setSelectedLocos(selected);

            const params = new URLSearchParams(searchParams);
            params.set("page", String(1));
            params.set("rows", String(10));
            setSearchParams(params, { replace: true });

            setFirst(0);
            setRows(10);

            setEventsQueryParams((prev: any) => ({
              ...prev,
              locoIds: selected.join(","),
              page_num: 1,
              total_entry_per_page: 10,
            }));

            saveFiltersToStorage({
              locos: selected,
              eventCategories: [...new Set(selectedEventInEventsDistributionChart)],
              subCategories: selectedSubCategories,
              treeSelection: selectedTree
            });
          }}
          placeholder="Select Locos"
          className="w-56"
          display="chip"
        />

        <TreeSelect
          value={selectedTree}
          options={eventTreeOptions}
          disabled={isEventCategoriesLoading}
          selectionMode="checkbox"
          display="chip"
          placeholder="Select Events"
          className="w-72"
          filter
          resetFilterOnHide
          onChange={(e) => {
            const value = e.value || {};

            setSelectedTree(value);

            const categories = new Set<string>();
            const subCategories = new Set<string>();

            Object.keys(value).forEach((key) => {
              if (!(value as any)[key]?.checked && !(value as any)[key]?.partialChecked) return;

              if (key.includes("||")) {
                const [category, sub] = key.split("||");

                categories.add(
                  category
                    .replaceAll("_", " ")
                    .toLowerCase()
                    .replace(/\b\w/g, c => c.toUpperCase())
                );

                subCategories.add(sub);
              } else {
                categories.add(
                  key
                    .replaceAll("_", " ")
                    .toLowerCase()
                    .replace(/\b\w/g, c => c.toUpperCase())
                );
              }
            });

            const selectedCategories = Array.from(categories);
            const selectedSubs = [...subCategories];

            const params = new URLSearchParams(searchParams);
            params.set("page", "1");
            params.set("rows", "10");
            setSearchParams(params, { replace: true });

            setSelectedEventInEventsDistributionChart(selectedCategories);
            setSelectedSubCategories(selectedSubs);

            setFirst(0);
            setRows(10);

            setEventsQueryParams((prev: any) => ({
              ...prev,
              eventCategories: [...new Set(selectedCategories)]
                .map((t: string) =>
                  t.replaceAll(" ", "_").replaceAll("/", "_").toUpperCase()
                )
                .join(","),
              subCategories: selectedSubs
                .map((t: string) =>
                  t.toUpperCase()
                )
                .join(","),
              page_num: 1,
              total_entry_per_page: 10,
            }));

            saveFiltersToStorage({
              locos: selectedLocos,
              eventCategories: [...new Set(selectedCategories)],
              subCategories: [...new Set(selectedSubs)],
              treeSelection: value
            });
          }}
        />

      </div>
    </div>

  );

  return (
    <div className="h-[calc(100vh-4rem)] p-6 flex-1 overflow-y-auto">
      <Toast ref={toast} position="top-right" />

      <BreadCrumb model={breadcrumb} />

      <div className="flex items-center justify-between w-full my-3">
        {/* <h2 className="text-xl font-medium">Events</h2> */}

        <div className="flex flex-col items-start relative ml-auto">
          <Calendar
            id="date"
            value={dates}
            onChange={(e) => { handleDateChange(e.value as (Date | null)[], 'fromCalendar'); }}
            selectionMode="range"
            readOnlyInput
            hideOnRangeSelection
            dateFormat="dd/mm/yy"
            numberOfMonths={2}
            placeholder="Select Date"
            className="w-full min-w-64"
            inputClassName="w-64 pr-10"
          />
          <IconCalendar size={18} stroke={1.5} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none z-10" />
        </div>
      </div>


      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">

        {/* ---------- ROW 1 : 3 STAT CARDS ---------- */}
        {eventsStatistics.map((event, index) => (
          <div className="md:col-span-4" key={event.label}>
            <Card className="h-full" key={index}>
              {index === 0 && !isTripDataLoading && (
                <Tooltip target=".trip-summary-tooltip" position="top">
                  <div className="text-sm">
                    <div>Aborted Trips: <b>{event.abortedTrips}</b></div>
                    <div>Completed Trips: <b>{event.completedTrips}</b></div>
                    <div>Open Trips: <b>{event.openTrips}</b></div>
                  </div>
                </Tooltip>
              )}
              <div className={`pl-5 relative ${index === 0 ? "trip-summary-tooltip cursor-pointer" : ""
                }`}>
                <span
                  className="absolute left-0 top-0 w-[.25rem] rounded-full h-full"
                  style={{ backgroundColor: event.color }}
                ></span>
                {isTripDataLoading ? (
                  <>
                    <Skeleton width="60%" height="1.5rem" className="mb-2" />
                    <Skeleton width="40%" height="1.5rem" />
                  </>
                ) : (
                  <>
                    <p className="text-gray-700 dark:text-gray-100">{event.label}</p>
                    <h2 className="text-3xl text-gray-900 dark:text-gray-100 font-bold">
                      {event.value}
                    </h2>
                  </>
                )}
              </div>
            </Card>
          </div>
        ))}

        <div className="md:col-span-6">
          <Card className="h-full" header="Events Distribution">
            <DonutChart
              data={eventsDistributionData}
              id="events-distribution"
              className="h-72"
              selectedEventInEventsDistributionChart={selectedEventInEventsDistributionChart}
            // onSelect={(type) => {
            //   if (!type) return;

            //   setSelectedEventInEventsDistributionChart((prev: string[]) => {
            //     let updated;

            //     if (prev.includes(type)) {
            //       updated = prev.filter(t => t !== type);
            //     } else {
            //       updated = [...prev, type];
            //     }

            //     setFirst(0);
            //     setRows(10);

            //     setEventsQueryParams((prevParams: any) => ({
            //       ...prevParams,
            //       page_num: 1,
            //       total_entry_per_page: 10,
            //       eventCategories: updated.length
            //         ? updated.map(t => t.replaceAll(' ', '_').toUpperCase())
            //         : null
            //     }));

            //     saveFiltersToStorage({
            //       locos: selectedLocos,
            //       eventCategories: updated,
            //     });

            //     return updated;
            //   });
            // }}
            />
          </Card>
        </div>

        <div className="md:col-span-6">
          <Card className="h-full" header={eventsOverTimeHeader}>

            <LineChart id="events-over-time" data={eventsOverTimeData} xOffset={true} isScroll />
          </Card>
        </div>

        {/* <div className="md:col-span-4">
          <Card className="h-full p-card-body-p-0" header="Events Distribution by Region">
            <IndiaRailwayMap className="h-74" eventPoints={eventPoints} selectedZone={selectedZone} onZoneChange={setSelectedZone} />
          </Card>
        </div> */}

        {/* <div className="md:col-span-6">
          <Card header="Top 10 Problematic Stations">
            <BarChart id='stations' data={stationBarChartData} className="h-80" yText="No. Of Events" xText="Station ID" stacked={true} barThickness={true} />
          </Card>
        </div> */}
        <div className="md:col-span-12">

          <Card header="Most Problematic Locos">
            <BarChart id='locos' data={locoBarChartData} className="h-80" yText="No. Of Events" xText="Loco ID" stacked={true} barThickness={true} />
          </Card>
        </div>


      </div>


      {/* ---------- ROW 4 : 1 CARD ---------- */}
      <div className="grid grid-cols-1 mb-6">
        <Card header={eventsTableHeader} className="p-card-body-p-0">
          <Table
            columns={eventTableColumns}
            data={eventTableData}
            showGridlines={true}
            paginator={true}
            isLoading={isFetching}
            first={first}
            rows={rows}
            totalRecords={totalEvents}
            renderCell={(rowData, field) => {
              if (field === 'eventCategory') {
                return <Tag value={rowData[field]} style={{
                  backgroundColor: getEventColor(rowData[field]),
                  borderColor: getEventColor(rowData[field]),
                  color: '#fff'
                }}></Tag>
              }
              if (field === 'action') {
                return <Button className="bg-gray-200 border-gray-200 text-gray-700 dark:bg-gray-700 dark:border-gray-700 dark:text-gray-200" onClick={() => handleEventView(rowData)} icon={<IconEye size={20} />} label="View" outlined />
              }
              if (field === 'subCategories') {
                return (
                  rowData[field] ?
                    <div className="flex gap-2">
                      {(rowData[field] ?? []).map((row: any) => (
                        <>
                          <Tag
                            value={row}
                            severity={getSeverity(row)}
                          />
                        </>))}
                    </div> : '-'
                );
              }
            }}
            onPageChangeExternal={(page_num, total_entry_per_page) => {
              setFirst((page_num - 1) * total_entry_per_page);
              setRows(total_entry_per_page);
              setEventsQueryParams((prev: any) => ({
                ...prev,
                page_num: page_num,
                total_entry_per_page: total_entry_per_page
              }));


              const params = new URLSearchParams(searchParams);
              params.set("page", String(page_num));
              params.set("rows", String(total_entry_per_page));
              setSearchParams(params, { replace: true });
            }}
          />
        </Card>
      </div>

    </div>
  );
}

export default Dashboard;
